import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaInfoCircle } from "react-icons/fa";
import {
  Row,
  Col,
  Spinner,
  Button,
  Modal,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";

import Back from "../../assets/svg/back.svg";
import Delete from "../../assets/svg/delete-po.svg";
import SearchWithAPI from "../common/searchbox";

import {
  createOrderApi,
  updateOrderApi,
  getOrderDetailsApi,
  addOrderAttachmentsBulkApi,
  getOrderAttachmentsApi,
  removeOrderAttachmentApi,
  getCustomerDetailsApi,
  getLeadDetailsApi,
  getOrgCurrencyApi,
} from "./order-api";
import "./order.css";
import SearchWithFreeTextAPI from "../common/searchbox-free-text";
import CommonDropdown from "../common/common-new-dropdown.jsx";
import {
  sanitizeInput,
  sanitizeNumericInput,
  sanitizeTextarea,
} from "../common/security-utils";
import { applyStateTaxFlags } from "../common/state-tax";
import FileUploadComponent from "../common/FileUploadComponent";
import AttachmentDeleteModal from "../common/AttachmentDeleteModal";

const ErrorMessage = ({ message }) => {
  if (!message) return null;
  return (
    <span
      style={{
        color: "#ff4444",
        fontSize: "12px",
        fontWeight: "400",
        display: "block",
        marginTop: "4px",
      }}
    >
      {sanitizeInput(String(message))}
    </span>
  );
};

// ⓘ icon with a breakdown tooltip rendered in a body portal so it is never
// clipped by the table/card overflow. `rows` is an array of [label, value,
// isTotal?] tuples shown above the icon.
const InfoTooltip = ({ rows }) => {
  const iconRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const openTooltip = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.top - 8, left: rect.right });
      setShow(true);
    }
  };
  const closeTooltip = () => setShow(false);

  return (
    <span
      ref={iconRef}
      className="total-info-wrap"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <FaInfoCircle className="total-info-icon" size={13} />
      {show &&
        createPortal(
          <span
            className="total-info-tooltip"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {rows.map(([label, value, isTotal], i) => (
              <span key={i} className={`tt-row${isTotal ? " tt-total" : ""}`}>
                <span>{label}</span>
                <span>{value}</span>
              </span>
            ))}
          </span>,
          document.body,
        )}
    </span>
  );
};

const OrderCreate = () => {
  const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiOrder = process.env.REACT_APP_ORDER_API_URL;
  const nextItemIdRef = useRef(1);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  const [orderId, setOrderId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Order Details
  const [customerOption, setCustomerOption] = useState(null);
  const [salesPersonOption, setSalesPersonOption] = useState(null);
  const [orderTypeOption, setOrderTypeOption] = useState(null);
  const [orderDate, setOrderDate] = useState("");
  const [currencyOption, setCurrencyOption] = useState(null);
  const [exchangeRate, setExchangeRate] = useState("0");
  const [paymentTypeOption] = useState(null);
  const [paymentStatusOption] = useState(null);

  // Additional Information
  const [title, setTitle] = useState("");
  const [projectOption, setProjectOption] = useState(null);
  const [description, setDescription] = useState("");

  // Address Information - Billing
  const [billingAddress, setBillingAddress] = useState("");
  const [billingCountryOption, setBillingCountryOption] = useState(null);
  const [billingStateOption, setBillingStateOption] = useState(null);
  const [billingCityOption, setBillingCityOption] = useState(null);
  const [billingZipCode, setBillingZipCode] = useState("");

  // Address Information - Shipping
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCountryOption, setShippingCountryOption] = useState(null);
  const [shippingStateOption, setShippingStateOption] = useState(null);
  const [shippingCityOption, setShippingCityOption] = useState(null);
  const [shippingZipCode, setShippingZipCode] = useState("");

  // Items
  const [itemType, setItemType] = useState("Product"); // Product or Service
  const [items, setItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  // Terms & Conditions and Notes
  const [termsConditions, setTermsConditions] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");

  // Totals
  const [subTotal, setSubTotal] = useState(0);
  const [subTotalWithOutTax, setSubTotalWithOutTax] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("Rs.");
  const [tdsOption, setTdsOption] = useState("TDS");
  const [tdsPercentage, setTdsPercentage] = useState("0");

  const [selectTaxOption, setSelectTaxOption] = useState(null);
  const [discountTotal, setDiscountTotal] = useState(0);
  const [adjustment, setAdjustment] = useState(0);
  const [total, setTotal] = useState(0);
  const [tdsCalcValue, setTdsCalcValue] = useState(0);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [attachmentUploadError, setAttachmentUploadError] = useState(null);
  const [attachmentUploadInProgress, setAttachmentUploadInProgress] = useState(false);
  const [attachmentDeleteModal, setAttachmentDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [pendingItemType, setPendingItemType] = useState(null);

  const [validations, setValidations] = useState({});
  const [groupedError, setGroupedError] = useState({});

  const getItemValidation = (rowId, field) => {
    const index = items.findIndex((item) => item.rowId === rowId);
    if (index === -1) return "";
    return validations[`items.${index}.${field}`];
  };

  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const sanitizeSignedNumericInput = (input, max = 999999999) => {
    if (input === null || input === undefined) return "";
    const raw = String(input);
    const cleaned = raw.replace(/[^0-9.-]/g, "").replace(/(?!^)-/g, "");

    const [head, ...tail] = cleaned.split(".");
    const normalized = tail.length > 0 ? `${head}.${tail.join("")}` : head;

    if (
      normalized === "" ||
      normalized === "-" ||
      normalized === "." ||
      normalized === "-."
    ) {
      return normalized;
    }

    // If input is still being typed (ends with . or incomplete), return as-is
    if (normalized.endsWith(".")) {
      return normalized;
    }

    const parsed = Number.parseFloat(normalized);
    if (Number.isNaN(parsed)) return "";

    const clamped = Math.max(-max, Math.min(parsed, max));
    return clamped.toString();
  };

  // Number of line items of the active type (discount is split across these).
  const activeItemCount = items.filter((i) => i.item_type === itemType).length;

  // Per-item discount (total discount split equally). Tax is charged on the
  // amount AFTER this discount is removed — same rule as quotation-create.
  const perItemDiscount =
    toNumber(discountTotal) > 0 && activeItemCount > 0
      ? toNumber(discountTotal) / activeItemCount
      : 0;
  const hasDiscount = toNumber(discountTotal) > 0;

  const money = (n) =>
    `${currencySymbol}${toNumber(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Break a line item into its parts: base, per-item discount, taxable amount,
  // per-tax amounts, and the discounted+taxed total.
  const getItemBreakdown = (item) => {
    const baseAmount = toNumber(item.rate) * toNumber(item.quantity);
    const taxableAmount = Math.max(baseAmount - perItemDiscount, 0);
    const cgst = toNumber(item.cgst);
    const sgst = toNumber(item.sgst);
    const igst = toNumber(item.igst);
    const cgstAmount = taxableAmount * (cgst / 100);
    const sgstAmount = taxableAmount * (sgst / 100);
    const igstAmount = taxableAmount * (igst / 100);
    const taxAmount = cgstAmount + sgstAmount + igstAmount;
    return {
      baseAmount,
      itemDiscount: perItemDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cgstAmount,
      sgstAmount,
      igstAmount,
      taxAmount,
      total: taxableAmount + taxAmount,
    };
  };

  const calculateItemTotal = (item) => getItemBreakdown(item).total.toFixed(2);

  // Aggregate GST across the active items (on the post-discount base).
  const gstTotals = items
    .filter((i) => i.item_type === itemType)
    .reduce(
      (acc, item) => {
        const bd = getItemBreakdown(item);
        acc.cgst += bd.cgstAmount;
        acc.sgst += bd.sgstAmount;
        acc.igst += bd.igstAmount;
        return acc;
      },
      { cgst: 0, sgst: 0, igst: 0 },
    );
  const totalTax = gstTotals.cgst + gstTotals.sgst + gstTotals.igst;

  const createEmptyItem = (type) => ({
    rowId: nextItemIdRef.current++,
    item_type: type,
    hsnCode: "",
    itemName: "",
    quantity: "",
    uom: "",
    rate: "",
    cgst: "",
    sgst: "",
    igst: "",
    totalAmount: "",
  });

  // Items handlers
  const addItem = () => {
    setItems((prev) => [...prev, createEmptyItem(itemType)]);
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((item) => item.rowId !== idx));
  };

  const updateItem = (rowId, key, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (item.rowId !== rowId) return item;
        const updated = { ...item, [key]: value };
        if (["rate", "quantity", "cgst", "sgst", "igst"].includes(key)) {
          updated.totalAmount = calculateItemTotal(updated);
        }
        return updated;
      }),
    );
  };

  const requestItemTypeChange = (nextType) => {
    if (nextType === itemType) return;
    setPendingItemType(nextType);
    setShowSwitchModal(true);
  };

  const confirmItemTypeChange = () => {
    if (!pendingItemType) return;
    setItems([createEmptyItem(pendingItemType)]);
    setItemType(pendingItemType);
    setPendingItemType(null);
    setShowSwitchModal(false);
  };

  const cancelItemTypeChange = () => {
    setPendingItemType(null);
    setShowSwitchModal(false);
  };

  // Copy billing address to shipping
  const copyBillingToShipping = () => {
    setShippingAddress(billingAddress);
    setShippingCountryOption(billingCountryOption);
    setShippingStateOption(billingStateOption);
    setShippingCityOption(billingCityOption);
    setShippingZipCode(billingZipCode);
  };

  // Dropdown handlers
  const handleCustomerDropdownChange = async (option) => {
    setCustomerOption(option);
    // Auto-fill customer details including addresses
    await fetchAndAutoFillCustomerDetails(option.value);
  };

  const fetchAndAutoFillCustomerDetails = async (customerId) => {
    try {
      const response = await getCustomerDetailsApi(customerId);

      if (response.data && response.data.success === true) {
        const customer = response.data.customer;

        // Auto-fill billing address
        setBillingAddress(customer.bill_address ?? "");
        setBillingCountryOption(customer.bill_country ?? null);
        setBillingStateOption(customer.bill_state ?? null);
        setBillingCityOption(customer.bill_city ?? null);
        setBillingZipCode(customer.bill_zipcode ?? "");

        // Auto-fill shipping address
        setShippingAddress(customer.ship_address ?? "");
        setShippingCountryOption(customer.ship_country ?? null);
        setShippingStateOption(customer.ship_state ?? null);
        setShippingCityOption(customer.ship_city ?? null);
        setShippingZipCode(customer.ship_zipcode ?? "");

        // Update state tax flags based on shipping state
        if (customer.ship_state?.value) {
          applyStateTaxFlags(customer.ship_state.value);
        }
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
    }
  };

  const handleSalesPersonDropdownChange = (option) => {
    setSalesPersonOption(option);
  };

  const handleOrderTypeDropdownChange = (option) => {
    setOrderTypeOption(option);
  };

  const handleCurrencyDropdownChange = (option) => {
    setCurrencyOption(option);
  };

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Symbol of the selected currency; falls back to ₹ when none is selected.
  const currencySymbol = currencyOption?.symbol
    ? decodeHtmlEntity(currencyOption.symbol)
    : "₹";

  // Organization's own currency — the Conversion Rate field is only shown
  // when the selected currency differs from this. Name/symbol are used to
  // show the "Total in {org currency}" row below the Total.
  const [orgCurrencyId, setOrgCurrencyId] = useState(null);
  const [orgCurrencyName, setOrgCurrencyName] = useState("");
  const [orgCurrencySymbol, setOrgCurrencySymbol] = useState("");
  const showConversionRate =
    orgCurrencyId != null &&
    currencyOption?.value != null &&
    String(orgCurrencyId) !== String(currencyOption.value);

  useEffect(() => {
    const fetchOrgCurrency = async () => {
      try {
        const response = await getOrgCurrencyApi();
        if (response?.data?.success) {
          setOrgCurrencyId(response.data.data?.org_currency_id ?? null);
          setOrgCurrencyName(response.data.data?.org_currency_name ?? "");
          setOrgCurrencySymbol(decodeHtmlEntity(response.data.data?.org_currency_symbol) || "");
        }
      } catch (err) {
        // Non-critical — Conversion Rate simply stays hidden if this fails.
      }
    };
    fetchOrgCurrency();
  }, []);

  const handleBillingCountryDropdownChange = (option) => {
    setBillingCountryOption(option);
    setBillingStateOption(null);
    setBillingCityOption(null);
  };

  const handleBillingStateDropdownChange = (option) => {
    setBillingStateOption(option);
    setBillingCityOption(null);
  };

  const handleShippingCountryDropdownChange = (option) => {
    setShippingCountryOption(option);
    setShippingStateOption(null);
    setShippingCityOption(null);
  };

  const handleShippingStateDropdownChange = (option) => {
    setShippingStateOption(option);
    setShippingCityOption(null);
  };

  const handleShippingCityDropdownChange = (option) => {
    setShippingCityOption(option);
    setShippingStateOption({
      label: option.state_name,
      value: option.state_id,
    });
    setShippingCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
  };

  const handleBillingCityDropdownChange = (option) => {
    setBillingCityOption(option);
    setBillingStateOption({ label: option.state_name, value: option.state_id });
    setBillingCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
  };

  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);

  const updateStateTaxFlags = async (stateId) => {
    await applyStateTaxFlags(stateId, {
      setIsIgst,
      setIsCgst,
      setIsSgst,
    });
  };

  useEffect(() => {
    if (shippingStateOption?.value) {
      updateStateTaxFlags(shippingStateOption.value);
    }
  }, [shippingStateOption]);

  // Calculate totals
  useEffect(() => {
    const activeItems = items.filter((item) => item.item_type === itemType);

    // Pre-tax subtotal (quantity × rate). Discount and TDS apply on this base.
    const preTaxSubTotal = activeItems.reduce(
      (sum, item) => sum + (toNumber(item.quantity * item.rate) || 0),
      0,
    );
    setSubTotalWithOutTax(preTaxSubTotal);
    setSubTotal(preTaxSubTotal);

    const discountValue = toNumber(discount);
    const discountTotalLocal =
      discountType === "%"
        ? (preTaxSubTotal * discountValue) / 100
        : discountValue;

    // GST across items, charged on the post-discount amount (equal split).
    const count = activeItems.length || 1;
    const itemDiscount = discountTotalLocal > 0 ? discountTotalLocal / count : 0;
    const totalItemTax = activeItems.reduce((sum, item) => {
      const base = toNumber(item.rate) * toNumber(item.quantity);
      const taxable = Math.max(base - itemDiscount, 0);
      const rate =
        toNumber(item.cgst) + toNumber(item.sgst) + toNumber(item.igst);
      return sum + taxable * (rate / 100);
    }, 0);

    const percentage = Number(tdsPercentage || 0);
    const afterDiscount = preTaxSubTotal - discountTotalLocal;

    // Grand total = (subtotal − discount) + item GST + adjustment (no TDS/TCS
    // — TDS/TCS is shown separately below the Total, not included in it).
    const finalTotal = afterDiscount + totalItemTax + toNumber(adjustment);

    // TDS/TCS is calculated from the without-tax (after-discount, pre-GST)
    // amount, but not added back into the Total.
    const tdsAmount = percentage > 0 ? afterDiscount * (percentage / 100) : 0;
    const tdsValue = toNumber(tdsAmount);

    setDiscountTotal(discountTotalLocal);
    setTdsCalcValue(tdsValue);
    setTotal(finalTotal);
  }, [items, itemType, discount, discountType, tdsOption, tdsPercentage, adjustment]);

  const submitOrder = async () => {
    setIsSaving(true);

    const payload = {
      customer_id: customerOption?.value || null,
      sales_person_id: salesPersonOption?.value || null,
      order_type: orderTypeOption?.value || null,
      order_date: sanitizeInput(orderDate),
      currency_id: currencyOption?.value || null,
      exchange_rate: sanitizeNumericInput(exchangeRate),
      payment_type: paymentTypeOption?.value || null,
      payment_status: paymentStatusOption?.value || null,
      title: sanitizeInput(title),
      project_id: projectOption?.value || null,
      description: sanitizeTextarea(description),
      bill_address: sanitizeTextarea(billingAddress),
      bill_country_id: billingCountryOption?.value || null,
      bill_state_id: billingStateOption?.value || null,
      bill_city_id: billingCityOption?.value || null,
      bill_zipcode: sanitizeInput(billingZipCode),
      ship_address: sanitizeTextarea(shippingAddress),
      ship_country_id: shippingCountryOption?.value || null,
      ship_state_id: shippingStateOption?.value || null,
      ship_city_id: shippingCityOption?.value || null,
      ship_zipcode: sanitizeInput(shippingZipCode),
      item_type: sanitizeInput(itemType),
      items: items.map((item) => ({
        id: item.id ? sanitizeInput(String(item.id)) : null,
        product_id: item.product?.value ? sanitizeInput(String(item.product.value)) : null,
        service_name: sanitizeInput(item.service_name ?? ""),
        hsn_no: sanitizeInput(item.hsnCode ?? ""),
        uom_id: item.unit?.value ? sanitizeInput(String(item.unit.value)) : null,
        quantity: sanitizeNumericInput(item.quantity),
        unit_price: sanitizeNumericInput(item.rate),
        cgst_percentage: sanitizeNumericInput(item.cgst),
        sgst_percentage: sanitizeNumericInput(item.sgst),
        igst_percentage: sanitizeNumericInput(item.igst),
      })),
      removed_items: removedItems,
      terms_condition: sanitizeTextarea(termsConditions),
      customer_note: sanitizeTextarea(customerNotes),
      sub_total: subTotal,
      discount_type: discountType === "%" ? "Percentage" : "Amount",
      discount_value: sanitizeNumericInput(discount),
      tds_option: tdsOption,
      tds_percentage: sanitizeNumericInput(
        tdsOption === "TDS" ? tdsPercentage : 0,
      ),
      tcs_percentage: sanitizeNumericInput(
        tdsOption === "TCS" ? tdsPercentage : 0,
      ),

      adjustment_amount: sanitizeSignedNumericInput(adjustment),
      total: total,
    };

    try {
      let response;
      // 🔒 VAPT: Check if retrying attachment upload (currentOrderId exists)
      const orderIdForUpdate = currentOrderId || orderId;

      if (orderIdForUpdate != null && isValidId(orderIdForUpdate)) {
        // If currentOrderId exists, it means we're retrying after attachment error
        response = await updateOrderApi(payload, orderIdForUpdate);
      } else {
        response = await createOrderApi(payload);
      }

      if (response.status === 200 && response.data.success === true) {
        const createdOrderId = orderId || response.data?.id || response.data?.order?.id;
        setCurrentOrderId(createdOrderId);
        setAttachmentUploadError(null);

        // Upload attachments if present (only new files, not existing ones)
        const newFilesToUpload = uploadedFiles.filter((file) => !file.isExisting);

        if (newFilesToUpload.length > 0 && createdOrderId) {
          try {
            setAttachmentUploadInProgress(true);
            const bulkPayload = newFilesToUpload.map((fileObj) => ({
              file_name: fileObj.name,
              file: fileObj.base64,
            }));

            const responseAttachment = await addOrderAttachmentsBulkApi(createdOrderId, bulkPayload);
            if (responseAttachment && responseAttachment.data.success === true) {
              setAttachmentUploadError(null);
              setAttachmentUploadInProgress(false);
              setSnackbarMessage("✓ Order created and attachments uploaded successfully");
              setSnackbarStatus(true);
              handleSnackbarShow();
              navigate(-1);
            } else {
              // Attachment upload failed but order created
              const errorMsg = "⚠ Order created but attachment upload failed. Please try uploading attachments again.";
              setAttachmentUploadError(errorMsg);
              setAttachmentUploadInProgress(false);
              setSnackbarMessage(errorMsg);
              setSnackbarStatus(false);
              handleSnackbarShow();
              // Stay on page to retry attachment upload
              return;
            }
          } catch (attachmentError) {
            // Order created but attachment upload failed
            const errorMsg = "⚠ Order created but attachment upload failed. Please try uploading attachments again.";
            setAttachmentUploadError(errorMsg);
            console.error("Attachment upload error:", attachmentError);
            setAttachmentUploadInProgress(false);
            setSnackbarMessage(errorMsg);
            setSnackbarStatus(false);
            handleSnackbarShow();
            // Stay on page to retry
            return;
          }
        } else {
          // No attachments to upload, navigate normally
          setSnackbarMessage(sanitizeInput(response.data.message ?? ""));
          setSnackbarStatus(Boolean(response.data.success));
          handleSnackbarShow();
          navigate(-1);
        }
      }

      if (response.status === 400) {
        setSnackbarMessage(sanitizeInput(response.data.message ?? ""));
        setSnackbarStatus(Boolean(response.data.success));
        handleSnackbarShow();

        const groupedErrorTemp = {};
        Object.entries(response.data.error).forEach(([key, value]) => {
          // match tickets.index.field
          const match = key.match(/^items\.(\d+)\.(.+)$/);

          if (match) {
            const index = match[1]; // e.g. "0"
            const field = match[2]; // e.g. "closing_date"

            if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
            groupedErrorTemp[index][field] = value;
          }
        });
        setGroupedError(groupedErrorTemp);

        const validationTemp = {};
        Object.entries(response.data.error).forEach(([key, value]) => {
          const safeValue =
            Array.isArray(value) && value.length > 0 ? value[0] : "";
          validationTemp[key] =
            typeof safeValue === "string" ? sanitizeInput(safeValue) : "";
        });
        setValidations(validationTemp);
      }
    } catch (err) {
      setSnackbarMessage("Failed to create order");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsSaving(false);
    }
  };

  const isValidId = (id) => {
    if (!id) return false;
    return /^[a-zA-Z0-9_-]+$/.test(String(id));
  };

  const fetchExistingAttachments = async (orderId) => {
    if (!isValidId(String(orderId))) return;
    try {
      const response = await getOrderAttachmentsApi(orderId);
      if (response?.data?.success === true) {
        const attachmentList = response?.data?.attachments ?? [];

        // Mark as existing attachments from server
        const existingAttachments = attachmentList.map((att) => ({
          id: att.id,
          name: sanitizeInput(att.file_name ?? ""),
          size: parseInt(att.file_size ?? "0"),
          type: "application/octet-stream",
          base64: att.file_path ?? "",
          isExisting: true,
          file_path: att.file_path ?? "",
          created_at: att.created_at ?? "",
        }));
        setUploadedFiles(existingAttachments);
      }
    } catch (err) {
      console.error("Failed to fetch attachments", err);
    }
  };

  const handleFilesAdd = (validFiles, invalidFiles) => {
    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      console.log(`✓ ${validFiles.length} file(s) uploaded successfully`);
    }

    if (invalidFiles.length > 0) {
      console.warn(`⚠ File(s) too large or invalid: ${invalidFiles.join(", ")}. Max size is 5MB.`);
    }
  };

  const handleFileRemove = (index) => {
    const fileToRemove = uploadedFiles[index];

    // If it's an existing attachment from server, show delete modal
    if (fileToRemove?.isExisting && fileToRemove?.id) {
      setAttachmentToDelete({ id: fileToRemove.id, name: fileToRemove.name, index });
      setAttachmentDeleteModal(true);
    } else {
      // New file (not yet saved), just remove from UI
      setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const confirmDeleteAttachment = async () => {
    try {
      setIsDeleting(true);
      const response = await removeOrderAttachmentApi(attachmentToDelete.id);
      if (response?.data?.success) {
        setUploadedFiles((prev) =>
          prev.filter((att) => att.id !== attachmentToDelete.id)
        );
       
        setSnackbarMessage("✓ Attachment deleted successfully");
      setSnackbarStatus(true);
      handleSnackbarShow();
        setAttachmentDeleteModal(false);
        setAttachmentToDelete(null);
      } else {
        setSnackbarMessage("Failed to delete attachment. Please try again.");
        setSnackbarStatus(false);
        handleSnackbarShow();
      }
    } catch (error) {
      setSnackbarMessage(`Error deleting attachment: ${error.message}`);
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteAttachment = () => {
    setAttachmentDeleteModal(false);
    setAttachmentToDelete(null);
  };

  // Note: Attachment upload is now handled in submitOrder with comprehensive error handling
  // This keeps the order created even if attachments fail to upload
  // User can retry without losing the order data

  useEffect(() => {
    if (id != null) {
      setOrderId(id);
      setCurrentOrderId(id);
      getOrderDetails();
      fetchExistingAttachments(id);
    } else if (!orderDate) {
      const today = new Date().toISOString().slice(0, 10);
      setOrderDate(sanitizeInput(today));
    }
  }, []);

  const getOrderDetails = async () => {
    try {
      const response = await getOrderDetailsApi(id);
      if (response?.data.success === true && response?.data?.order != null) {
        const orderDetails = response.data.order;
        const resolvedItemType = orderDetails.item_type || "Product";

        setOrderId(orderDetails.id ?? id);
        setCustomerOption(orderDetails.customer ?? null);
        setSalesPersonOption(orderDetails.sales_person ?? null);
        setOrderTypeOption(
          orderDetails.order_type
            ? { value: orderDetails.order_type, label: orderDetails.order_type }
            : null,
        );
        // Extract YYYY-MM-DD format from orderDetails.order_date (handles ISO or timestamp formats)
        const dateString =
          orderDetails.order_date?.split("T")[0]?.split(" ")[0] || "";
        setOrderDate(sanitizeInput(dateString));
        // Carry the currency symbol so it renders on edit (falls back to ₹).
        if (orderDetails.currency) {
          setCurrencyOption({
            ...orderDetails.currency,
            symbol:
              orderDetails.currency.symbol ??
              orderDetails.currency.currency_symbol ??
              orderDetails.currency_symbol,
          });
        } else {
          setCurrencyOption(null);
        }
        setExchangeRate(sanitizeNumericInput(orderDetails.exchange_rate ?? "0"));
        if (orderDetails.organization_currency_id != null) {
          setOrgCurrencyId(orderDetails.organization_currency_id);
        }
        if (orderDetails.org_currency_name) {
          setOrgCurrencyName(orderDetails.org_currency_name);
        }
        if (orderDetails.org_currency_symbol) {
          setOrgCurrencySymbol(decodeHtmlEntity(orderDetails.org_currency_symbol) || "");
        }
        setTitle(sanitizeInput(orderDetails.title ?? ""));
        setProjectOption(orderDetails.project ?? null);
        setDescription(sanitizeTextarea(orderDetails.description ?? ""));
        setItemType(sanitizeInput(resolvedItemType));

        setBillingAddress(
          sanitizeTextarea(orderDetails.bill_address?.address ?? ""),
        );
        setBillingCountryOption(orderDetails.bill_address?.country ?? null);
        setBillingStateOption(orderDetails.bill_address?.state ?? null);
        setBillingCityOption(orderDetails.bill_address?.city ?? null);
        setBillingZipCode(
          sanitizeInput(orderDetails.bill_address?.zipcode ?? ""),
        );

        setShippingAddress(
          sanitizeTextarea(orderDetails.ship_address?.address ?? ""),
        );
        setShippingCountryOption(orderDetails.ship_address?.country ?? null);
        setShippingStateOption(orderDetails.ship_address?.state ?? null);
        setShippingCityOption(orderDetails.ship_address?.city ?? null);
        setShippingZipCode(
          sanitizeInput(orderDetails.ship_address?.zipcode ?? ""),
        );

        setTermsConditions(
          sanitizeTextarea(orderDetails.terms_condition ?? ""),
        );
        setCustomerNotes(sanitizeTextarea(orderDetails.customer_note ?? ""));

        if (orderDetails.discount_type === "Percentage") {
          setDiscountType("%");
          const discountValue = Number(orderDetails.discount_percentage ?? 0);
          setDiscount(discountValue);
        } else {
          setDiscountType("Rs.");
          const discountValue = Number(orderDetails.discount_amount ?? 0);
          setDiscount(discountValue);
        }

        const tdsPercentage = Number(orderDetails.tds_percentage ?? 0);
        const tcsPercentage = Number(orderDetails.tcs_percentage ?? 0);
        if (tcsPercentage > 0) {
          setTdsOption("TCS");
          setTdsPercentage(tcsPercentage);
        } else {
          setTdsOption("TDS");
          setTdsPercentage(tdsPercentage);
        }

        setAdjustment(Number(orderDetails.adjustment_amount ?? 0));
        setTotal(Number(orderDetails.total_amount ?? 0));

        const lineItems = Array.isArray(orderDetails.items)
          ? orderDetails.items
          : [];
        if (lineItems.length > 0) {
          const mappedItems = lineItems.map((lineItem, index) => {
            const baseItem = {
              rowId: index,
              id: lineItem.id,
              item_type: resolvedItemType,
              hsnCode: sanitizeInput(
                lineItem.hsn_no ?? lineItem.product?.hsn ?? lineItem.product?.hsn_no ?? "",
              ),
              itemName: "",
              quantity: sanitizeNumericInput(lineItem.quantity ?? ""),
              uom: lineItem.uom ?? null,
              unit: lineItem.uom ?? null,
              rate: sanitizeNumericInput(lineItem.unit_price ?? ""),
              cgst: sanitizeNumericInput(lineItem.cgst_percentage ?? ""),
              sgst: sanitizeNumericInput(lineItem.sgst_percentage ?? ""),
              igst: sanitizeNumericInput(lineItem.igst_percentage ?? ""),
              totalAmount: sanitizeNumericInput(lineItem.total_amount ?? ""),
            };

            if (resolvedItemType === "Product") {
              baseItem.product = lineItem.product ?? null;
            } else {
              baseItem.service_name = sanitizeInput(
                lineItem.service_name ?? "",
              );
            }

            if (!baseItem.totalAmount) {
              baseItem.totalAmount = calculateItemTotal(baseItem);
            }
            return baseItem;
          });
          nextItemIdRef.current = mappedItems.length;
          setItems(mappedItems);

          const subTotalFromItemsWithoutTax = mappedItems.reduce((sum, item) => {
            return sum + toNumber(item.quantity * item.rate);
          }, 0);
          setSubTotalWithOutTax(subTotalFromItemsWithoutTax);

          const subTotalFromItems = mappedItems.reduce((sum, item) => {
            return sum + toNumber(item.totalAmount);
          }, 0);
          setSubTotal(subTotalFromItems);
        } else {
          setItems([createEmptyItem(resolvedItemType)]);
        }
      }
    } catch (err) {
      setSnackbarMessage("Failed to fetch order details.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };
  const getErrorMsg = (index, field) => {
    if (field === "product_id") {
      if (groupedError[index] != null) {
        if (groupedError[index].product_id != null) {
          return (
            groupedError[index].product_id[0]?.replace(
              `items.${index}.product_id`,
              "product",
            ) ?? ""
          );
        }
      }
    }
    if (field === "service_name") {
      if (groupedError[index] != null) {
        if (groupedError[index].service_name != null) {
          return (
            groupedError[index].service_name[0]?.replace(
              `items.${index}.service_name`,
              "service",
            ) ?? ""
          );
        }
      }
    }
    if (field === "uom_id") {
      if (groupedError[index] != null) {
        if (groupedError[index].uom_id != null) {
          return (
            groupedError[index].uom_id[0]?.replace(
              `items.${index}.uom_id`,
              "unit",
            ) ?? ""
          );
        }
      }
    }

    if (field === "quantity") {
      if (groupedError[index] != null) {
        if (groupedError[index].quantity != null) {
          return (
            groupedError[index].quantity[0]?.replace(
              `items.${index}.quantity`,
              "quantity",
            ) ?? ""
          );
        }
      }
    }

    if (field === "unit_price") {
      if (groupedError[index] != null) {
        if (groupedError[index].unit_price != null) {
          return (
            groupedError[index].unit_price[0]?.replace(
              `items.${index}.unit_price`,
              "unit price",
            ) ?? ""
          );
        }
      }
    }

    if (field === "igst_percentage") {
      if (groupedError[index] != null) {
        if (groupedError[index].igst_percentage != null) {
          return (
            groupedError[index].igst_percentage[0]?.replace(
              `items.${index}.igst_percentage`,
              "igst percentage",
            ) ?? ""
          );
        }
      }
    }
    if (field === "sgst_percentage") {
      if (groupedError[index] != null) {
        if (groupedError[index].sgst_percentage != null) {
          return (
            groupedError[index].sgst_percentage[0]?.replace(
              `items.${index}.sgst_percentage`,
              "sgst percentage",
            ) ?? ""
          );
        }
      }
    }

    if (field === "cgst_percentage") {
      if (groupedError[index] != null) {
        if (groupedError[index].cgst_percentage != null) {
          return (
            groupedError[index].cgst_percentage[0]?.replace(
              `items.${index}.cgst_percentage`,
              "cgst percentage",
            ) ?? ""
          );
        }
      }
    }
    return "";
  };
  return (
    <div className="page-container ">
      <div className="order-header px-2 mt-2 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <img
            src={Back}
            alt="back"
            className="back-icon"
            onClick={() => navigate(-1)}
          />
          <div className="page-title">{id ? "Edit Order" : "New Order"}</div>
        </div>

        <div>
          <Button
            variant="light"
            className="btn-cancel me-2"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            className="btn-save"
            onClick={submitOrder}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
      <div className="order-page">
        <div className="d-flex flex-wrap col-12">
          {/* Left Column */}
          <div className="col-md-8 col-sm-12 mt-2">
            {/* Order Details Section */}
            <div className="order-card">
              <h5 className="card-title">Order details</h5>

              <Row>
                <Col md={12}>
                  <label className="field-label">
                    Customer<span className="required">*</span>
                  </label>
                  <SearchWithAPI
                    onDropdownChange={handleCustomerDropdownChange}
                    apiUrl={apiUrl + "get_customers"}
                    params={{}}
                    placeholder={"Select"}
                    selectedOption={customerOption}
                  />
                  <ErrorMessage message={validations.customer_id} />
                </Col>

                <Col md={6} className="mt-3">
                  <label className="field-label">Sales Person</label>
                  <SearchWithAPI
                    onDropdownChange={handleSalesPersonDropdownChange}
                    apiUrl={apiAccUrl + `get_employee`}
                    params={{}}
                    placeholder={"Select"}
                    selectedOption={salesPersonOption}
                  />
                  <ErrorMessage message={validations.sales_person_id} />
                </Col>

                <Col md={6} className="mt-3">
                  <label className="field-label">Order Type</label>
                  <SearchWithFreeTextAPI
                    onDropdownChange={(option) => {
                      setOrderTypeOption(option);
                    }}
                    onChangeSearch={(term) => {
                      setOrderTypeOption({ value: term, label: term });
                    }}
                    apiUrl={apiUrl + `get_order_type`}
                    params={{}}
                    searchLimit={0}
                    placeholder={"Search order type..."}
                    selectedValue={orderTypeOption?.label || ""}
                  />
                  {/* <SearchWithAPI
                    onDropdownChange={handleOrderTypeDropdownChange}
                    apiUrl={apiUrl + "get_order_type"}
                    params={{}}
                    placeholder={"Select"}
                    selectedOption={orderTypeOption}
                  /> */}
                  <ErrorMessage message={validations.order_type} />
                </Col>

                <Col md={6} className="mt-3">
                  <label className="field-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    placeholder="dd/mm/yyyy hh:mm"
                    value={orderDate}
                    onChange={(e) =>
                      setOrderDate(sanitizeInput(e.target.value))
                    }
                  />
                  <ErrorMessage message={validations.order_date} />
                </Col>

                <Col md={6} className="mt-3">
                  <label className="field-label">Currency</label>
                  <SearchWithAPI
                    onDropdownChange={handleCurrencyDropdownChange}
                    apiUrl={apiUrl + "get_currency"}
                    params={{}}
                    placeholder={"Select"}
                    selectedOption={currencyOption}
                  />
                  <ErrorMessage message={validations.currency} />
                </Col>

                {showConversionRate && (
                  <Col md={6} className="mt-3">
                    <label className="field-label">Conversion Rate</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter conversion rate"
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(sanitizeNumericInput(e.target.value))}
                    />
                    <ErrorMessage message={validations.exchange_rate} />
                  </Col>
                )}

                {/*  <Col md={6} className="mt-3">
                <label className="field-label">Payment Type</label>
                <SearchWithAPI
                  onDropdownChange={handlePaymentTypeDropdownChange}
                  apiUrl={apiUrl + "get_payment_types"}
                  params={{}}
                  placeholder={"Select"}
                  selectedOption={paymentTypeOption}
                />
                <ErrorMessage message={validations.payment_type} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">Payment Status</label>
                <SearchWithAPI
                  onDropdownChange={handlePaymentStatusDropdownChange}
                  apiUrl={apiUrl + "get_payment_statuses"}
                  params={{}}
                  placeholder={"Select"}
                  selectedOption={paymentStatusOption}
                />
                <ErrorMessage message={validations.payment_status} />
              </Col> */}
              </Row>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-md-4 col-sm-12 mt-2">
            {/* Additional Information */}
            <div className="order-card">
              <h5 className="card-title">Additional Information</h5>

              <label className="field-label">Title </label>
              <input
                className="form-control"
                placeholder="Select"
                value={title}
                onChange={(e) => setTitle(sanitizeTextarea(e.target.value))}
              />
              <ErrorMessage message={validations.title} />
              <label className="field-label mt-2">Project</label>
              <SearchWithAPI
                onDropdownChange={(option) => setProjectOption(option)}
                apiUrl={apiUrl + "get_projects"}
                params={{}}
                placeholder={"Select"}
                selectedOption={projectOption}
              />
              <ErrorMessage message={validations.project_id} />
              <label className="field-label mt-2">Description</label>
              <textarea
                className="form-control"
                rows={1}
                placeholder="Enter here..."
                value={description}
                onChange={(e) =>
                  setDescription(sanitizeTextarea(e.target.value))
                }
              />
              <ErrorMessage message={validations.description} />
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap col-12 mt-2">
          {/* Address Information Section - Separate Cards */}
          <div className="col-md-6 col-sm-12 mt-2">
            <div className="order-card h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="card-title mb-0">Billing Address</h5>
              </div>

              <div className="order-address-section">
                <div className="mb-3">
                  <label className="field-label">
                    Address<span className="required">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Enter here..."
                    value={billingAddress}
                    onChange={(e) =>
                      setBillingAddress(sanitizeTextarea(e.target.value))
                    }
                  />
                  <ErrorMessage message={validations.bill_address} />
                </div>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        Country<span className="required">*</span>
                      </label>
                      <SearchWithAPI
                        onDropdownChange={
                          handleBillingCountryDropdownChange
                        }
                        apiUrl={apiAccUrl + "get_country"}
                        params={{}}
                        placeholder={"Select"}
                        selectedOption={billingCountryOption}
                      />
                      <ErrorMessage message={validations.bill_country_id} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        State<span className="required">*</span>
                      </label>
                      <SearchWithAPI
                        onDropdownChange={handleBillingStateDropdownChange}
                        apiUrl={apiAccUrl + "get_state"}
                        params={{
                          country_id: billingCountryOption?.value || "",
                        }}
                        placeholder={"Select"}
                        selectedOption={billingStateOption}
                      />
                      <ErrorMessage message={validations.bill_state_id} />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        City<span className="required">*</span>
                      </label>
                      <SearchWithAPI
                        onDropdownChange={handleBillingCityDropdownChange}
                        apiUrl={apiAccUrl + "get_city"}
                        params={{
                          state_id:
                            billingStateOption != null
                              ? billingStateOption.value
                              : "",
                        }}
                        placeholder={"Select"}
                        selectedOption={billingCityOption}
                      />
                      <ErrorMessage message={validations.bill_city_id} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        Zip code<span className="required">*</span>
                      </label>
                      <input
                        className="form-control"
                        placeholder="Enter here..."
                        value={billingZipCode}
                        onChange={(e) =>
                          setBillingZipCode(sanitizeInput(e.target.value))
                        }
                      />
                      <ErrorMessage message={validations.bill_zipcode} />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-sm-12 mt-2">
            <div className="order-card h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="card-title mb-0">Shipping Address</h5>
                <span
                  className="copy-billing-link"
                  onClick={copyBillingToShipping}
                >
                  Copy billing address
                </span>
              </div>

              <div className="order-address-section">
                <div className="mb-3">
                  <label className="field-label">
                    Address<span className="required">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Enter here..."
                    value={shippingAddress}
                    onChange={(e) =>
                      setShippingAddress(sanitizeTextarea(e.target.value))
                    }
                  />
                  <ErrorMessage message={validations.ship_address} />
                </div>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        Country<span className="required">*</span>
                      </label>
                      <SearchWithAPI
                        onDropdownChange={
                          handleShippingCountryDropdownChange
                        }
                        apiUrl={apiAccUrl + "get_country"}
                        params={{}}
                        placeholder={"Select"}
                        selectedOption={shippingCountryOption}
                      />
                      <ErrorMessage message={validations.ship_country_id} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        State<span className="required">*</span>
                      </label>
                      <SearchWithAPI
                        onDropdownChange={handleShippingStateDropdownChange}
                        apiUrl={apiAccUrl + "get_state"}
                        params={{
                          country_id: shippingCountryOption?.value || "",
                        }}
                        placeholder={"Select"}
                        selectedOption={shippingStateOption}
                      />
                      <ErrorMessage message={validations.ship_state_id} />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        City<span className="required">*</span>
                      </label>
                      <SearchWithAPI
                        onDropdownChange={handleShippingCityDropdownChange}
                        apiUrl={apiAccUrl + "get_city"}
                        params={{
                          state_id:
                            shippingStateOption != null
                              ? shippingStateOption.value
                              : "",
                        }}
                        placeholder={"Select"}
                        selectedOption={shippingCityOption}
                      />
                      <ErrorMessage message={validations.ship_city_id} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="field-label">
                        Zip code<span className="required">*</span>
                      </label>
                      <input
                        className="form-control"
                        placeholder="Enter here..."
                        value={shippingZipCode}
                        onChange={(e) =>
                          setShippingZipCode(sanitizeInput(e.target.value))
                        }
                      />
                      <ErrorMessage message={validations.ship_zipcode} />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          {/* Items Section - Full Width */}
        </div>

        <div className="order-card mt-3">
          <div className="d-flex align-items-center mb-3">
            <h5 className="card-title mb-0 me-3">Items :</h5>
            <div className="item-type-toggle">
              <label
                className={`item-type-option ${itemType === "Product" ? "active" : ""}`}
              >
                <input
                  type="radio"
                  name="itemType"
                  value="Product"
                  checked={itemType === "Product"}
                  onChange={(e) => requestItemTypeChange(e.target.value)}
                />
                <span className="item-type-text">Product</span>
              </label>
              <label
                className={`item-type-option ${itemType === "Service" ? "active" : ""}`}
              >
                <input
                  type="radio"
                  name="itemType"
                  value="Service"
                  checked={itemType === "Service"}
                  onChange={(e) => requestItemTypeChange(e.target.value)}
                />
                <span className="item-type-text">Service</span>
              </label>
            </div>
          </div>

          <div className="items-table-wrapper">
            <table className="items-table">
              <thead>
                <tr>
                  <th>
                    {itemType === "Service" ? "SERVICE NAME" : "ITEM NAME"}
                  </th>
                  <th>HSN CODE</th>
                  <th>QUANTITY</th>
                  <th>UOM</th>
                  <th>RATE</th>
                  <th>AMOUNT</th>
                  {hasDiscount && <th>DISCOUNT</th>}
                  {isCgst && <th>CGST</th>}
                  {isSgst && <th>SGST</th>}
                  {isIgst && <th>IGST</th>}
                  <th>TOTAL AMOUNT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items
                  .filter((item) => item.item_type === itemType)
                  .map((item, index) => (
                    <tr key={item.rowId}>
                      {itemType === "Product" && (
                        <td>
                          <div>
                            {" "}
                            <CommonDropdown
                              apiUrl={`${apiUrl}get_products?term=`}
                              placeholder="Select"
                              value={item.product}
                              onSelect={(option) => {
                                const itemTemp = [...items];
                                const index = itemTemp.findIndex(
                                  (i) => i.rowId === item.rowId,
                                );
                                itemTemp[index].product = option;
                                // Prefill editable HSN from the selected product.
                                itemTemp[index].hsnCode = sanitizeInput(
                                  option?.hsn_no ?? option?.hsn ?? "",
                                );
                                // Prefill UOM from the item master.
                                if (option?.uom?.value) {
                                  itemTemp[index].unit = option.uom;
                                }
                                setItems(itemTemp);
                              }}
                            />
                          </div>
                          <ErrorMessage
                            message={getErrorMsg(index, "product_id")}
                          />
                        </td>
                      )}
                      {itemType === "Service" && (
                        <td>
                          <div>
                            <SearchWithFreeTextAPI
                              onDropdownChange={(option) => {
                                const itemTemp = [...items];
                                const index = itemTemp.findIndex(
                                  (i) => i.rowId === item.rowId,
                                );
                                itemTemp[index].service_name = sanitizeInput(
                                  option.value,
                                );
                                 itemTemp[index].hsnCode = sanitizeInput(
                                        option?.hsn_no ?? option?.hsn ?? "",
                                      );
                                setItems(itemTemp);
                              }}
                              onChangeSearch={(term) => {
                                const itemTemp = [...items];
                                const index = itemTemp.findIndex(
                                  (i) => i.rowId === item.rowId,
                                );
                                itemTemp[index].service_name =
                                  sanitizeInput(term);
                                setItems(itemTemp);
                              }}
                              apiUrl={apiUrl + `get_services`}
                              params={{}}
                              searchLimit={0}
                              placeholder={"Search searvice..."}
                              selectedValue={item.service_name}
                            />
                            <ErrorMessage
                              message={getErrorMsg(index, "service_name")}
                            />
                          </div>
                        </td>
                      )}
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="HSN Code"
                            value={item.hsnCode ?? ""}
                            onChange={(e) =>
                              updateItem(
                                item.rowId,
                                "hsnCode",
                                sanitizeInput(e.target.value),
                              )
                            }
                          />
                        </td>
                      
                      

                      <td>
                        <input
                          type="text"
                          className="table-input"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(
                              item.rowId,
                              "quantity",
                              e.target.value === ""
                                ? ""
                                : sanitizeNumericInput(e.target.value),
                            )
                          }
                        />
                        <ErrorMessage
                          message={getErrorMsg(index, "quantity")}
                        />
                      </td>
                      <td>
                        <div>
                          <CommonDropdown
                            apiUrl={`${apiAccUrl}get_uom?term=`}
                            placeholder="Select"
                            value={item.unit}
                            onSelect={(option) => {
                              const itemTemp = [...items];
                              const index = itemTemp.findIndex(
                                (i) => i.rowId === item.rowId,
                              );
                              itemTemp[index].unit = option;
                              setItems(itemTemp);
                            }}
                          />
                          <ErrorMessage
                            message={getErrorMsg(index, "uom_id")}
                          />
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-input"
                          placeholder={`${currencySymbol} Amount`}
                          value={item.rate}
                          onChange={(e) =>
                            updateItem(
                              item.rowId,
                              "rate",
                              e.target.value === ""
                                ? ""
                                : sanitizeNumericInput(e.target.value),
                            )
                          }
                        />
                        <ErrorMessage
                          message={getErrorMsg(index, "unit_price")}
                        />
                      </td>
                      {/* Amount = Quantity × Rate (read-only) */}
                      <td>
                        <input
                          type="text"
                          className="table-input"
                          readOnly
                          value={getItemBreakdown(item).baseAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        />
                      </td>
                      {hasDiscount && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            readOnly
                            value={getItemBreakdown(item).itemDiscount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          />
                        </td>
                      )}
                      {isCgst && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="Value %"
                            value={item.cgst}
                            onChange={(e) =>
                              updateItem(
                                item.rowId,
                                "cgst",
                                e.target.value === ""
                                  ? ""
                                  : sanitizeNumericInput(e.target.value),
                              )
                            }
                          />
                          <ErrorMessage
                            message={getErrorMsg(index, "cgst_percentage")}
                          />
                        </td>
                      )}
                      {isSgst && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="Value %"
                            value={item.sgst}
                            onChange={(e) =>
                              updateItem(
                                item.rowId,
                                "sgst",
                                e.target.value === ""
                                  ? ""
                                  : sanitizeNumericInput(e.target.value),
                              )
                            }
                          />
                          <ErrorMessage
                            message={getErrorMsg(index, "sgst_percentage")}
                          />
                        </td>
                      )}
                      {isIgst && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="Value %"
                            value={item.igst}
                            onChange={(e) =>
                              updateItem(
                                item.rowId,
                                "igst",
                                e.target.value === ""
                                  ? ""
                                  : sanitizeNumericInput(e.target.value),
                              )
                            }
                          />
                          <ErrorMessage
                            message={getErrorMsg(index, "igst_percentage")}
                          />
                        </td>
                      )}
                      <td>
                        {(() => {
                          const bd = getItemBreakdown(item);
                          const rows = [
                            ["Amount", money(bd.baseAmount)],
                            ...(bd.itemDiscount > 0
                              ? [["Discount", `- ${money(bd.itemDiscount)}`]]
                              : []),
                            ["Taxable", money(bd.taxableAmount)],
                            ...(bd.cgst > 0
                              ? [[`CGST (${bd.cgst}%)`, `+ ${money(bd.cgstAmount)}`]]
                              : []),
                            ...(bd.sgst > 0
                              ? [[`SGST (${bd.sgst}%)`, `+ ${money(bd.sgstAmount)}`]]
                              : []),
                            ...(bd.igst > 0
                              ? [[`IGST (${bd.igst}%)`, `+ ${money(bd.igstAmount)}`]]
                              : []),
                            ["Total", money(bd.total), true],
                          ];
                          return (
                            <div className="table-input d-flex align-items-center justify-content-between gap-1">
                              <span>
                                {bd.total.toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                              <InfoTooltip rows={rows} />
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        {items.length > 1 && (
                          <button
                            className="delete-row-btn"
                            onClick={() => {
                              if (item.id) {
                                setRemovedItems((prev) => [...prev, item.id]);
                              }
                              removeItem(item.rowId);
                            }}
                            type="button"
                          >
                            <img
                              src={Delete}
                              alt="delete"
                              className="delete-icon"
                            />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <ErrorMessage message={validations.items} />
          {/* Totals Card inside Items - Right Side */}
          <div className="row mt-4">
            <div className="col-md-6">
              <div className="add-item-button mb-3">
                <Button variant="link" onClick={addItem}>
                  + Add Item
                </Button>
              </div>
              {/* Terms & Conditions */}
              <div className="mb-4">
                <h6 className="section-subtitle">Terms & Conditions</h6>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter here..."
                  value={termsConditions}
                  onChange={(e) =>
                    setTermsConditions(sanitizeTextarea(e.target.value))
                  }
                />
              </div>

              {/* Customer Notes */}
              <div>
                <h6 className="section-subtitle">Customer Notes</h6>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter here..."
                  value={customerNotes}
                  onChange={(e) =>
                    setCustomerNotes(sanitizeTextarea(e.target.value))
                  }
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="totals-card-inline">
                <div className="total-row">
                  <span className="total-label">Sub Total</span>
                  <span className="total-value">
                    {currencySymbol}
                    {subTotalWithOutTax.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <div className="total-row-with-input">
                  <span className="total-label d-inline-flex align-items-center gap-1">
                    Discount
                    {hasDiscount && (
                      <InfoTooltip
                        rows={[
                          ["On amount", money(subTotalWithOutTax)],
                          [
                            "Entered",
                            discountType === "%"
                              ? `${toNumber(discount)}%`
                              : money(discount),
                          ],
                          ["Discount amount", `- ${money(discountTotal)}`],
                          ...(activeItemCount > 0
                            ? [
                                [
                                  `Per item (÷${activeItemCount})`,
                                  `- ${money(perItemDiscount)}`,
                                ],
                              ]
                            : []),
                        ]}
                      />
                    )}
                  </span>
                  <div className="total-input-group">
                    <input
                      type="text"
                      className="form-control discount-input"
                      placeholder={`${currencySymbol} Amount`}
                      value={discount}
                      onChange={(e) =>
                        setDiscount(
                          e.target.value === ""
                            ? ""
                            : sanitizeNumericInput(e.target.value),
                        )
                      }
                    />
                    <select
                      className="form-select discount-select"
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value)}
                    >
                      <option value="Rs.">Rs.</option>
                      <option value="%">%</option>
                    </select>
                  </div>
                  <span className="discount-value">
                    -{discountTotal.toFixed(2)}
                  </span>
                </div>

                {totalTax > 0 && (
                  <div className="total-row">
                    <span className="total-label d-inline-flex align-items-center gap-1">
                      GST
                      <InfoTooltip
                        rows={[
                          [
                            "On amount",
                            money(subTotalWithOutTax - toNumber(discountTotal)),
                          ],
                          ...(gstTotals.cgst > 0
                            ? [["CGST", money(gstTotals.cgst)]]
                            : []),
                          ...(gstTotals.sgst > 0
                            ? [["SGST", money(gstTotals.sgst)]]
                            : []),
                          ...(gstTotals.igst > 0
                            ? [["IGST", money(gstTotals.igst)]]
                            : []),
                          ["Total GST", money(totalTax), true],
                        ]}
                      />
                    </span>
                    <span className="total-value">{money(totalTax)}</span>
                  </div>
                )}
                <div className="total-row-with-input">
                    <span className="total-label d-inline-flex align-items-center gap-1">
                      Adjustment
                      <InfoTooltip
                        rows={[
                          [
                            "Adjustment",
                            `${toNumber(adjustment) < 0 ? "- " : ""}${money(Math.abs(toNumber(adjustment)))}`,
                          ],
                          ["Applied to", "Grand total"],
                        ]}
                      />
                    </span>
                    <input
                      type="text"
                      className="form-control adjustment-input"
                      placeholder="0.00"
                      value={adjustment}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        const val =
                          rawValue === "" ? "" : sanitizeNumericInput(rawValue);
                        setAdjustment(val);
                      }}
                    />
                    <span className="discount-value">{adjustment}</span>
                  </div>
                  <span
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    fontWeight: "400",
                  }}
                >
                  {validations.adjust_amount}
                </span>

                
                <div className="total-row total-final">
                  <span className="total-label-final">Total ({currencySymbol})</span>
                  <span className="total-value-final">
                    {currencySymbol}
                    {total.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* Organization currency differs from the selected currency —
                    show the base-currency equivalent (Total × Exchange Rate). */}
                {showConversionRate && (
                  <div className="total-row">
                    <span className="total-label d-inline-flex align-items-center gap-1">
                      Total in {orgCurrencyName || "Org Currency"}
                      <InfoTooltip
                        rows={[
                          ["Total Amount", `${currencySymbol}${total.toFixed(2)}`],
                          ["Exchange Rate", `× ${Number(exchangeRate) || 1}`],
                          [
                            "Converted Amount",
                            `${orgCurrencySymbol} ${(total * (Number(exchangeRate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                            true,
                          ],
                        ]}
                      />
                    </span>
                    <span className="total-value">
                      {orgCurrencySymbol} {(total * (Number(exchangeRate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                {/* TDS/TCS + Adjustment shown below the Total — informational
                    only, NOT included in the Total figure above. */}
                
                <div className="total-row-with-input">
                  <div className="tds-radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="tdsOption"
                        value="TDS"
                        checked={tdsOption === "TDS"}
                        onChange={(e) => setTdsOption(e.target.value)}
                      />
                      <span>TDS</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="tdsOption"
                        value="TCS"
                        checked={tdsOption === "TCS"}
                        onChange={(e) => setTdsOption(e.target.value)}
                      />
                      <span>TCS</span>
                    </label>
                    <InfoTooltip
                      rows={[
                        ["Type", tdsOption],
                        [
                          "On amount",
                          money(toNumber(subTotalWithOutTax) - toNumber(discountTotal)),
                        ],
                        ["Rate", `${toNumber(tdsPercentage)}%`],
                        [
                          tdsOption === "TDS" ? "TDS" : "TCS",
                          `${tdsOption === "TDS" ? "- " : "+ "}${money(tdsCalcValue)}`,
                          true,
                        ],
                      ]}
                    />
                  </div>
                  <input
                    type="number"
                    placeholder="Enter %"
                    className="form-control adjustment-input"
                    value={tdsPercentage || ""}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      let val =
                        rawValue === ""
                          ? ""
                          : toNumber(sanitizeNumericInput(rawValue));

                      if (val > 100) val = 100; // max limit
                      if (val < 0) val = 0; // prevent negative

                      setTdsPercentage(val);
                    }}
                  />
                  <span className="discount-value">
                    {tdsOption === "TDS"
                      ? ` - ${tdsCalcValue.toFixed(2)}`
                      : ` ${tdsCalcValue.toFixed(2)}`}
                  </span>
                </div>
                <span
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    fontWeight: "400",
                  }}
                >
                  {validations.tds_percentage}
                </span>
                <span
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    fontWeight: "400",
                  }}
                >
                  {validations.tcs_percentage}
                </span>
                
                

                {/* Receivable = Total ∓ TDS/TCS (TDS subtracts, TCS adds). */}
                <div className="total-row">
                  <span className="total-label" style={{ fontWeight: 600 }}>Receivable</span>
                  <span className="total-value" style={{ fontWeight: 700 }}>
                    {currencySymbol}
                    {(tdsOption === "TCS" ? total + tdsCalcValue : total - tdsCalcValue).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

         

          <div className="row mt-4">
            
          </div>

          {/* Attachments Section */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="form-section">
                <h6 className="section-subtitle">Attachments</h6>

                {/* Attachment Error Message */}
                {attachmentUploadError && (
                  <div style={{
                    padding: "12px",
                    marginBottom: "16px",
                    backgroundColor: "#fee2e2",
                    border: "1px solid #fca5a5",
                    borderRadius: "6px",
                    color: "#7f1d1d",
                    fontSize: "13px",
                  }}>
                    <strong>⚠ Attachment Error:</strong> {attachmentUploadError}
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "#991b1b" }}>
                      💡 Try uploading the attachments again by clicking the upload area below, then save the order.
                    </div>
                  </div>
                )}

                <FileUploadComponent
                  uploadedFiles={uploadedFiles}
                  onFilesAdd={handleFilesAdd}
                  onFileRemove={handleFileRemove}
                  maxSize={5}
                  acceptedFormats=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />

                {/* {uploadedFiles && uploadedFiles.length > 0 && (
                  <div style={{ marginTop: "24px" }}>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                      gap: "16px",
                    }}>
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            padding: "12px",
                            backgroundColor: "#f9fafb",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            justifyContent: "space-between",
                          }}
                        >
                          <div style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "6px",
                            backgroundColor: "#10b981",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            fontWeight: "700",
                            flexShrink: 0,
                          }}>
                            {(file.file_name || file.name).split(".").pop().toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#1f2937",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                              {file.file_name || file.name}
                            </div>
                            <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                              {file.file_size ? `${file.file_size} MB` : file.size ? `${(file.size / 1024).toFixed(2)} KB` : ""}
                            </div>
                          </div>
                          <button
                            onClick={() => handleFileRemove(index, file)}
                            style={{
                              backgroundColor: "#fee2e2",
                              border: "1px solid #fca5a5",
                              color: "#dc2626",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "500",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#fca5a5";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#fee2e2";
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <Modal show={showSwitchModal} onHide={cancelItemTypeChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {" "}
          If you switch tab, current tab values will be removed
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={cancelItemTypeChange}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmItemTypeChange}>
            Switch
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Attachment Delete Modal */}
      <AttachmentDeleteModal
        isOpen={attachmentDeleteModal}
        attachment={attachmentToDelete}
        isDeleting={isDeleting}
        onConfirm={confirmDeleteAttachment}
        onCancel={cancelDeleteAttachment}
      />
    </div>
  );
};

export default OrderCreate;
